import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { ChatIconName } from '../chat-icon/chat-icon.component';

/**
 * Centered illustration + title + text, with an optional action. Used for
 * "no conversations", "pick a conversation" and "no chat account".
 *
 * The illustration is inline SVG drawn with the theme tokens (two soft
 * bubbles on a tinted disc, three dots in each). `icon` is kept for the
 * callers but no longer drawn: the floating icon never sat right on the bubble.
 */
@Component({
  selector: 'chat-empty-state',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
<div class="chat-empty">
  <div class="chat-empty__art" aria-hidden="true">
    <svg viewBox="0 0 160 120" width="160" height="120" fill="none" focusable="false">
      <circle cx="80" cy="62" r="52" class="chat-empty__disc"/>
      <path d="M26 30h64a10 10 0 0 1 10 10v22a10 10 0 0 1-10 10H50l-14 12V72h-10A10 10 0 0 1 16 62V40a10 10 0 0 1 10-10z" class="chat-empty__bubble-back"/>
      <path d="M70 52h64a10 10 0 0 1 10 10v22a10 10 0 0 1-10 10h-10v12l-14-12H70a10 10 0 0 1-10-10V62a10 10 0 0 1 10-10z" class="chat-empty__bubble-front"/>
      <circle cx="40" cy="51" r="3.5" class="chat-empty__dot"/>
      <circle cx="54" cy="51" r="3.5" class="chat-empty__dot"/>
      <circle cx="68" cy="51" r="3.5" class="chat-empty__dot"/>
      <circle cx="132" cy="24" r="4" class="chat-empty__spark"/>
      <circle cx="22" cy="96" r="3" class="chat-empty__spark"/>
      <circle cx="93" cy="73" r="3.5" class="chat-empty__dot chat-empty__dot--front"/>
      <circle cx="107" cy="73" r="3.5" class="chat-empty__dot chat-empty__dot--front"/>
      <circle cx="121" cy="73" r="3.5" class="chat-empty__dot chat-empty__dot--front"/>
      <circle cx="146" cy="100" r="2.5" class="chat-empty__spark"/>
    </svg>
  </div>
  <h2 class="chat-empty__title">{{ title }}</h2>
  <p *ngIf="text" class="chat-empty__text">{{ text }}</p>
  <button *ngIf="actionLabel" type="button" class="chat-empty__action" (click)="action.emit()">{{ actionLabel }}</button>
</div>`,
  styles: [`
:host { display: flex; flex-grow: 1; min-height: 0; }
.chat-empty { flex-grow: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 10px; padding: 32px 24px; text-align: center; animation: chat-empty-in .3s ease-out; }
.chat-empty__art { position: relative; width: 160px; height: 120px; margin-bottom: 4px; }
.chat-empty__disc { fill: rgba(var(--chat-accent-rgb), .08); }
.chat-empty__bubble-back { fill: var(--chat-surface); stroke: var(--chat-line); stroke-width: 1.5; }
.chat-empty__bubble-front { fill: var(--chat-accent); }
.chat-empty__dot { fill: rgba(var(--chat-accent-rgb), .35); }
.chat-empty__spark { fill: rgba(var(--chat-accent-rgb), .25); }
.chat-empty__dot--front { fill: rgba(255, 255, 255, .85); }
.chat-empty__title { margin: 0; font-size: 16px; font-weight: 600; color: var(--chat-heading); }
.chat-empty__text { margin: 0; max-width: 30em; font-size: 13px; line-height: 1.55; color: var(--chat-muted); }
.chat-empty__action { display: inline-flex; align-items: center; justify-content: center; min-height: 40px; padding: 0 18px; margin-top: 8px; border: 0; border-radius: var(--chat-radius); background: var(--chat-accent); color: var(--chat-on-accent); font: inherit; font-size: 13px; font-weight: 500; cursor: pointer; transition: filter var(--chat-motion) ease; }
.chat-empty__action:hover { filter: brightness(1.08); }
.chat-empty__action:focus-visible { outline: 2px solid var(--chat-ink); outline-offset: 2px; }
@keyframes chat-empty-in { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
@media (prefers-reduced-motion: reduce) { .chat-empty { animation: none; } }
`]
})
export class ChatEmptyStateComponent {
  @Input() icon: ChatIconName = 'chat';
  @Input() title = '';
  @Input() text = '';
  @Input() actionLabel = '';
  @Output() action = new EventEmitter<void>();
}
